import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import type { BookingLanguageCode } from '../../config/i18n';
import {
  getLocaleFromPath,
  isClientHubNavigationLocale,
  isServicesPath,
  localeHubPath,
  localizeServicesPath,
} from '../../config/localeRoutes';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import './LanguageSwitcher.css';

type LanguageSwitcherProps = {
  className?: string;
};

export function LanguageSwitcher({ className = '' }: LanguageSwitcherProps) {
  const { t } = useTranslation('common');
  const { currentLanguage, changeLanguage, availableLanguages } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const handleLanguageChange = async (lang: BookingLanguageCode) => {
    await changeLanguage(lang);
    setOpen(false);

    const pathLocale = getLocaleFromPath(location.pathname);
    const isLocaleHub =
      location.pathname === '/' ||
      (pathLocale !== null &&
        (location.pathname === localeHubPath(pathLocale) ||
          location.pathname === `${localeHubPath(pathLocale)}/`));

    if (isLocaleHub && isClientHubNavigationLocale(lang)) {
      const target = localeHubPath(lang);
      const normalizedCurrent = location.pathname.replace(/\/$/, '') || '/';
      const normalizedTarget = target.replace(/\/$/, '') || '/';
      if (normalizedTarget !== normalizedCurrent) {
        navigate(target);
      }
      return;
    }

    // Métier /services/… : navigation locale même sans flags prod (#1120 QA r5)
    if (isServicesPath(location.pathname) && isClientHubNavigationLocale(lang)) {
      const newPath = localizeServicesPath(location.pathname, lang);
      if (newPath && newPath !== location.pathname) {
        navigate(newPath);
      }
    }
  };

  return (
    <>
      <button
        type="button"
        className={`language-switcher__globe ${className}`.trim()}
        onClick={() => setOpen(true)}
        aria-label={t('languageSwitcher.openButton')}
        title={t('languageSwitcher.openButton')}
      >
        <Globe size={18} strokeWidth={2} aria-hidden />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="language-switcher__dialog sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('languageSwitcher.modalTitle')}</DialogTitle>
            <DialogDescription>{t('languageSwitcher.modalDescription')}</DialogDescription>
          </DialogHeader>
          <ul className="language-switcher__list" role="listbox" aria-label={t('languageSwitcher.ariaLabel')}>
            {availableLanguages.map((lang) => {
              const isActive = currentLanguage === lang.code;
              return (
                <li key={lang.code}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={isActive}
                    className={`language-switcher__option${isActive ? ' language-switcher__option--active' : ''}`}
                    onClick={() => void handleLanguageChange(lang.code as BookingLanguageCode)}
                  >
                    <span className="language-switcher__option-flag" aria-hidden>
                      {lang.flag}
                    </span>
                    <span className="language-switcher__option-label">{lang.name}</span>
                    <span className="language-switcher__option-code">{lang.code.toUpperCase()}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default LanguageSwitcher;
